import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Stack } from '@mui/system'
import React from 'react'
import { SettingsBox } from 'src/Elements/Data'
import { makeStyles } from 'tss-react/mui'
import { Typography } from '@e3dc-react/shell/Elements/Typography'
import { TabButton, TabButtonGroupBase } from 'src/Components/TabButton'

const useStyles = makeStyles()(theme => ({
  settingsBoxStack: {
    flexDirection: 'row',
    display: 'flex',
    gap: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      gap: theme.spacing(2),
      padding: `${theme.spacing(2)} 0`,
    },
  },
  itemStack: {
    flexDirection: 'column',
    flex: 0.5,
    gap: theme.spacing(2),
    justifyContent: 'space-between',
  },
  itemDescriptionRow: {
    flexDirection: 'column',
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
    },
  },

  itemDescriptionText: {
    color: theme.palette.common.white,
    textAlign: 'center',
    padding: `${theme.spacing(1)} 0`,
    width: '100%',
  },
  disabled: {
    textDecoration: 'line-through',
    backgroundColor: theme.palette.grey[500],
  },
}))

//#region Storage Option Settings box
export interface StorageOptionSettingsBoxProps {
  title: string
  description: string
  onToggleButtonAction: (value: boolean) => void
  activeToggleButton: number
  disabled: boolean
  options: StorageExplanationItemProps[]
}
/**
 * Reusable Settingsbox with a title, description and toggle-buttons (yes/no)
 * and two descriptive elements for the explanation of the yes-/no-options
 */
export const StorageOptionSettingsBox: React.FC<StorageOptionSettingsBoxProps> = ({
  title,
  description,
  onToggleButtonAction,
  activeToggleButton,
  disabled,
  options,
}) => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()

  return (
    <SettingsBox
      title={title}
      description={description}
      disabled={disabled}
      suffix={
        <TabButtonGroupBase defaultTabIndex={activeToggleButton} disabled={disabled}>
          <TabButton onClick={() => onToggleButtonAction(false)}>{t('common.no')}</TabButton>
          <TabButton onClick={() => onToggleButtonAction(true)}>{t('common.yes')}</TabButton>
        </TabButtonGroupBase>
      }
    >
      <Stack className={classes.settingsBoxStack}>
        {options.map((options, index) => (
          <StorageExplanationItem
            key={`${options.description}${index}`}
            description={options.description}
            descriptionItems={options.descriptionItems}
          />
        ))}
      </Stack>
    </SettingsBox>
  )
}
//#endregion

//#region Storage DescriptionRow
export interface StorageDescriptionItem {
  label: string
  color: string
  disabled?: boolean
}

export interface StorageExplanationItemProps {
  description: string
  descriptionItems: StorageDescriptionItem[]
}

/** A component with explanation about the specific solar- or boost-mode option */
const StorageExplanationItem: React.FC<StorageExplanationItemProps> = ({ description, descriptionItems }) => {
  const { classes, cx } = useStyles()
  return (
    <Stack className={classes.itemStack}>
      <Typography
        variant="body2"
        component="div"
        components={{
          bold: <Typography variant="body2" sx={{ fontWeight: 700 }} />,
        }}
      >
        {description}
      </Typography>

      <Stack className={classes.itemDescriptionRow}>
        {descriptionItems.map(descriptionItem => {
          return (
            <Typography
              variant="caption"
              key={descriptionItem.label}
              sx={{ backgroundColor: descriptionItem.color }}
              className={cx(classes.itemDescriptionText, descriptionItem.disabled && classes.disabled)}
            >
              {descriptionItem.label.toUpperCase()}
            </Typography>
          )
        })}
      </Stack>
    </Stack>
  )
}
//#endregion
